import { useTranslation } from "react-i18next"
import { Box, Typography, useTheme } from "@mui/material"

import type { AlertsSummaryEntity } from "types/alerts.types"

import SeverityDot from "widgets/plots/SeverityDot"
import { CELL_BORDERS } from "./styles"

// The gender is the label's, so "affected" agrees with it in Spanish
const ENTITY_LABELS: Record<
  AlertsSummaryEntity,
  { key: string; gender: "male" | "female" }
> = {
  powerline: { key: "entities.POWERLINES", gender: "female" },
  tower: { key: "entities.TOWERS", gender: "female" },
  device: { key: "entities.DEVICES", gender: "male" },
}

interface EntityStatProps {
  entity: AlertsSummaryEntity
  affected: number
  isFirst: boolean
}

const EntityStat = ({ entity, affected, isFirst }: EntityStatProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <Box
      data-testid={`${entity}-alerts-stat`}
      sx={{
        ...CELL_BORDERS,
        borderLeftWidth: { xs: isFirst ? 0 : 1, md: 1 },
        // Shares the rows with the other stats, so a label on two lines keeps the numbers aligned
        display: "grid",
        gridRow: "span 4",
        gridTemplateRows: "subgrid",
        p: { xs: 1.5, md: 2.5 },
        // On a card this narrow the longest label can't fit, so the columns stop growing for it
        "@container (max-width: 310px)": { minWidth: 0 },
      }}
    >
      <Typography
        variant="overlineDense"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.75,
          "@container (max-width: 310px)": { minWidth: 0 },
        }}
      >
        <Box
          component="span"
          sx={{ display: "flex", alignItems: "center", height: "1.4em" }}
        >
          <SeverityDot
            color={affected ? theme.palette.error.main : theme.palette.secondary.main}
          />
        </Box>
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {t(ENTITY_LABELS[entity].key)}
        </Box>
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "1.375rem", md: "1.5rem" },
          fontWeight: "fontWeightBold",
          lineHeight: 1,
          mt: 1,
          color: affected ? "text.primary" : "neutral.600",
        }}
      >
        {affected}
      </Typography>
      <Typography variant="caption" color="neutral.600" component="p" mt={1}>
        {t("alerts_summary.AFFECTED", {
          context: ENTITY_LABELS[entity].gender,
          count: affected,
        })}
      </Typography>
    </Box>
  )
}

export default EntityStat
